// apps/client/src/characters/avatar.ts — a dressed doll on the Kenney rig, with clip cross-fades,
// procedural overlays, emote FX and a held model (§8.5; port of look-demo avatar.ts). The dressed doll is baked into one skinned mesh
// (doll.ts `bakeDoll`): about 30 parts would cost about 30 draw calls per doll, twice over on `high` with its shadow (spec §8.7 budget).
import type { MoveAnim } from '@whiskerton/shared';
import * as THREE from 'three';
import { ANIMS, type ActionAnim as Anim, type AnimSpec } from './anims';
import { bakeDoll, bakedToyMaterial, dress, holdInHand, RIG_HEIGHT, toyMaterial, type DollBone, type DollSpec } from './doll';
import { OVERLAYS } from './overlays';

export type ActionAnim = Anim;
export interface RigSource { scene: THREE.Object3D; clips: THREE.AnimationClip[] }
const BONES: readonly DollBone[] = ['torso', 'head', 'arm-left', 'arm-right', 'leg-left', 'leg-right'];
const FADE = 0.2;
const FX_SCALE = 2.2;     // the heart, bang and zzz read as a speck at 1× at the default camera distance (playtest)
const WALK_PACE = 3.4, RUN_PACE = 6.4;   // the clips' authored speeds; tuning.move holds the gameplay speeds
const LOOPING: AnimSpec = { clip: '', length: 'loop' };
const qParent = new THREE.Quaternion(), qRoot = new THREE.Quaternion(), qTilt = new THREE.Quaternion(), vAxis = new THREE.Vector3();

let blobMaterial: THREE.MeshBasicMaterial | null = null;
function contactBlob(size: number): THREE.Mesh {
  if (!blobMaterial) {
    const n = 64, data = new Uint8Array(n * n * 4);
    for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
      const d = Math.hypot(x - n / 2 + 0.5, y - n / 2 + 0.5) / (n / 2);
      const a = Math.max(0, 1 - d) ** 1.6 * 0.45;
      data.set([84, 58, 30, Math.round(a * 255)], (y * n + x) * 4);   // DESIGN.md contact blob rgba(84,58,30,.45)
    }
    const tex = new THREE.DataTexture(data, n, n);
    tex.needsUpdate = true;
    blobMaterial = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 });
  }
  const m = new THREE.Mesh(new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2), blobMaterial);
  m.position.y = 0.02;
  m.renderOrder = -1;
  m.userData.blob = true;                 // out of the GTAO prepass, which would draw the whole plane as a dark square (engine/renderer.ts)
  return m;
}

export class Avatar {
  readonly root = new THREE.Group();
  private readonly rig: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private readonly bones: Record<DollBone, THREE.Object3D>;
  private readonly rest: Record<DollBone, THREE.Quaternion>;
  private readonly fxHeight: number;
  private current: THREE.AnimationAction | null = null;
  private move: MoveAnim = 'idle';
  private act: { name: Anim; t: number; onDone?: () => void } | null = null;
  private fx: THREE.Object3D | null = null;
  private held: { wrap: THREE.Group; model: THREE.Object3D; base: THREE.Quaternion } | null = null;
  private lean = 0;
  private time = 0;

  static create(rig: RigSource, spec: DollSpec, height: number): Avatar { return new Avatar(rig, spec, height); }

  private constructor(src: RigSource, spec: DollSpec, height: number) {
    this.rig = src.scene;
    dress(this.rig, spec);
    bakeDoll(this.rig, bakedToyMaterial());              // unpatched: residents are bent on the CPU (game.ts); villagers re-bake patched
    this.rig.scale.setScalar(height / RIG_HEIGHT);
    this.root.add(this.rig, contactBlob(height * 0.6));
    this.fxHeight = height + 0.35;
    this.bones = Object.fromEntries(BONES.map((b) => [b, this.rig.getObjectByName(b)!])) as Record<DollBone, THREE.Object3D>;
    this.rest = Object.fromEntries(BONES.map((b) => [b, this.bones[b].quaternion.clone()])) as Record<DollBone, THREE.Quaternion>;
    this.mixer = new THREE.AnimationMixer(this.rig);
    for (const clip of src.clips) this.actions.set(clip.name, this.mixer.clipAction(clip));
    this.fade(ANIMS.idle.clip, LOOPING, 0);
  }

  setMove(anim: MoveAnim, speed: number): void {
    if (this.act && ANIMS[this.act.name].length === 'untilMove' && (anim === 'walk' || anim === 'run')) this.finish();
    this.move = anim;
    if (!this.act) this.fade(ANIMS[anim].clip, LOOPING);
    const pace = anim === 'run' ? RUN_PACE : WALK_PACE;
    this.mixer.timeScale = anim === 'walk' || anim === 'run' ? Math.max(0.65, speed / pace) : 1;
  }

  play(action: Anim, onDone?: () => void): void {
    if (this.act) this.finish();
    const spec = ANIMS[action];
    this.act = { name: action, t: 0, onDone };
    this.fade(spec.clip, spec, FADE, true);
    if (spec.fx) this.showFx(spec.fx);
  }

  /** `model` is in world units (rev 4): a wrapper undoes the rig's height / RIG_HEIGHT scale. */
  hold(model: THREE.Object3D | null, hand: 'left' | 'right' | 'both' = 'right'): void {
    if (this.held) { this.held.wrap.removeFromParent(); this.held.model.removeFromParent(); }
    this.held = null;
    if (!model) return;
    const wrap = new THREE.Group();
    wrap.scale.setScalar(1 / this.rig.scale.x);
    wrap.add(model);
    holdInHand(this.rig, wrap, hand === 'left' ? 'left' : 'right');
    this.held = { wrap, model, base: model.quaternion.clone() };
  }
  /** Writes the held model's tip (a rod's `userData.tip`) in world space; false when nothing with a tip is held. The tip may sit on
   *  a child: the asset service wraps a code-built model in its own root group. */
  heldTip(out: THREE.Vector3): boolean {
    let tip: THREE.Object3D | undefined;
    this.held?.model.traverse((o) => { tip ??= o.userData.tip as THREE.Object3D | undefined; });
    if (!tip) return false;
    tip.getWorldPosition(out);
    return true;
  }
  /** Tilts the held model forward by `rad` about the avatar's right axis (a rod held out over the water while a line is out); 0 = as held. */
  leanHeld(rad: number): void { this.lean = rad; }

  update(dt: number): void {
    this.time += dt;
    // Overlays add to the pose, so start each frame from the rest pose for any bone a clip doesn't key.
    for (const b of BONES) this.bones[b].quaternion.copy(this.rest[b]);
    this.mixer.update(dt);
    if (this.act) {
      const spec = ANIMS[this.act.name];
      this.act.t += dt;
      if (spec.overlay) OVERLAYS[spec.overlay](this.act.t, this.bones, Math.min(1, this.act.t / FADE));
      if (typeof spec.length === 'number' && this.act.t >= spec.length) this.finish();
    } else {
      const o = ANIMS[this.move].overlay;
      if (o) OVERLAYS[o](this.time, this.bones, 1);
    }
    if (this.held) {
      const { wrap, model, base } = this.held;
      if (this.lean === 0) model.quaternion.copy(base);
      else {                                                   // world tilt about the avatar's right, expressed in the hand's frame
        wrap.updateWorldMatrix(true, false);
        wrap.getWorldQuaternion(qParent);
        vAxis.set(1, 0, 0).applyQuaternion(this.root.getWorldQuaternion(qRoot));
        qTilt.setFromAxisAngle(vAxis, this.lean);
        model.quaternion.copy(qParent).invert().multiply(qTilt).multiply(qParent).multiply(base);
      }
    }
    if (this.fx) { this.fx.position.y = this.fxHeight + Math.sin(this.time * 3) * 0.04; this.fx.rotation.y += dt; }
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.rig);
    this.root.removeFromParent();
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && m.geometry) m.geometry.dispose();
      if (m.name === 'baked' && (m as THREE.SkinnedMesh).isSkinnedMesh) (m as THREE.SkinnedMesh).skeleton.dispose();   // its bone texture
    });
  }

  private finish(): void {
    const done = this.act?.onDone;
    this.act = null;
    this.fx?.removeFromParent();
    this.fx = null;
    this.fade(ANIMS[this.move].clip, LOOPING);
    done?.();
  }

  /** Cross-fades to `clip`, configuring its loop mode and speed from `spec`. `restart` replays a clip that is already current. */
  private fade(clip: string, spec: AnimSpec, fade = FADE, restart = false): THREE.AnimationAction | null {
    const next = this.actions.get(clip);
    if (!next) return null;
    const once = typeof spec.length === 'number';
    next.setLoop(once ? THREE.LoopOnce : THREE.LoopRepeat, Infinity);
    next.clampWhenFinished = once;
    next.timeScale = spec.timeScale ?? 1;
    if (next !== this.current) {
      next.reset().fadeIn(fade).play();
      this.current?.fadeOut(fade);
      this.current = next;
    } else if (restart || !next.isRunning()) {
      next.reset().play();
    }
    return next;
  }

  private showFx(kind: 'bang' | 'heart' | 'zzz'): void {
    const g = new THREE.Group();
    if (kind === 'bang') {
      const bar = new THREE.Mesh(new THREE.CapsuleGeometry(0.03, 0.12, 4, 8), toyMaterial('#EE7A5B'));
      bar.position.y = 0.1;
      const dot = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 8), toyMaterial('#EE7A5B'));
      g.add(bar, dot);
    } else if (kind === 'heart') {
      for (const sx of [-1, 1]) {
        const lobe = new THREE.Mesh(new THREE.SphereGeometry(0.05, 12, 10), toyMaterial('#E07BAA'));
        lobe.position.set(sx * 0.04, 0.03, 0);
        g.add(lobe);
      }
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.075, 0.1, 12), toyMaterial('#E07BAA'));
      tip.rotation.z = Math.PI; tip.position.y = -0.03; g.add(tip);
    } else {
      for (let i = 0; i < 3; i++) {
        const z = new THREE.Mesh(new THREE.TorusGeometry(0.025 + i * 0.01, 0.008, 6, 4), toyMaterial('#A3A1DA'));
        z.position.set(i * 0.06, i * 0.07, 0);
        g.add(z);
      }
    }
    g.position.y = this.fxHeight;
    g.scale.setScalar(FX_SCALE);
    this.fx = g;
    this.root.add(g);
  }
}
